/**
 * Which result a run shows for each sample and each attribute. The reviewer's decision wins over the AI's, here
 * and in the working paper (PDF / Excel), which is built from these same functions.
 */

export type AttrStatus = 'passed' | 'failed' | 'warning';

/** A sample × attribute result as the results screen holds it. */
export interface SampleCell {
  sampleId?: string | null;
  status?: string | null;
  aiResult?: string | null;
  humanResult?: string | null;
  finalResult?: string | null;
}

/** Stored values ('pass', 'fail') in the form the screen compares against ('passed', 'failed'). */
export function normalizeSampleStatus(s?: string | null): string | null | undefined {
  if (!s) return s;
  if (s === 'pass') return 'passed';
  if (s === 'fail') return 'failed';
  return s;
}

/** The result shown for a sample: a just-saved override, then the stored final / reviewer result, then the AI's. */
export function sampleDisplayStatus(cell?: SampleCell | null, localFinal?: string | null): string | null {
  if (!cell) return null;
  const raw = localFinal || cell.finalResult || cell.humanResult || cell.aiResult || cell.status;
  return normalizeSampleStatus(raw) ?? null;
}

/** Whether the reviewer has decided this sample (so the result is theirs, not the AI's). */
export function sampleHasReviewerOverride(cell?: SampleCell | null, localFinal?: string | null): boolean {
  if (!cell) return false;
  return !!(localFinal || cell.finalResult || cell.humanResult);
}

const REVIEWER_LABEL: Record<string, string> = {
  passed: 'Pass', pass: 'Pass', failed: 'Exception', fail: 'Exception', exception: 'Exception', n_a: 'N/A',
};

/** The label of a reviewer's decision ('Pass', 'Exception', 'N/A'); 'Overridden' for anything else. */
export function reviewerLabel(status?: string | null): string {
  return (status && REVIEWER_LABEL[status]) || 'Overridden';
}

/** An attribute status in the canonical set, or null when it is not one of them. */
export function normalizeAttrStatus(s?: string | null): AttrStatus | null {
  const v = (s || '').toLowerCase();
  if (v === 'pass' || v === 'passed') return 'passed';
  if (v === 'fail' || v === 'failed' || v === 'exception') return 'failed';
  if (v === 'warning' || v === 'inconclusive' || v === 'needs_review') return 'warning';
  return null;
}

/** An attribute result as the API returns it (only the fields used here). */
export interface AttributeResult {
  id?: string;
  attribute_ref?: string | null;
  label?: string | null;
  status?: string | null;
  ai_status?: string | null;
  overall_status?: string | null;
  human_status?: string | null;
  findings_summary?: string | null;
  detailed_findings?: string | null;
  human_detailed_findings?: string | null;
  sample_results?: Array<{ attributes?: Record<string, unknown> | null }> | null;
}

/** Edits saved on the screen but not yet in `attr` (optimistic state). */
export interface AttributeEdits {
  human_status?: string | null;
  human_detailed_findings?: string | null;
}

export function attributeAiStatus(attr: AttributeResult): AttrStatus | null {
  return normalizeAttrStatus(attr.ai_status ?? attr.overall_status ?? attr.status);
}

export function attributeHumanStatus(attr: AttributeResult, edits: AttributeEdits = {}): string | null {
  return edits.human_status ?? attr.human_status ?? null;
}

/** The attribute verdict shown: the reviewer's, else the AI's ('' when neither). */
export function attributeDisplayStatus(attr: AttributeResult, edits: AttributeEdits = {}): string {
  return attributeHumanStatus(attr, edits) ?? attributeAiStatus(attr) ?? '';
}

/** The attribute's finding: the AI summary, and the reviewer's text when they wrote one (which is then shown). */
export function attributeFinding(attr: AttributeResult, edits: AttributeEdits = {}) {
  const ai = attr.findings_summary || '';
  const human = edits.human_detailed_findings ?? attr.human_detailed_findings ?? null;
  return { ai, human, shown: human || ai, observations: human ? '' : (attr.detailed_findings || '') };
}

/** Samples of the attribute whose check did not complete (timed out or failed): "re-run the test". */
export function notCompletedCount(attr: AttributeResult): number {
  return (attr.sample_results || []).filter((sr) => sr.attributes?._incomplete).length;
}
